import { useState } from 'react';
import { Box, Stack, Group, Badge, Table, TextInput, Select, ActionIcon, Menu, Avatar } from '@mantine/core';
import {
  IconSearch,
  IconDotsVertical,
  IconEye,
  IconMail,
  IconPhone,
  IconUser,
} from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockCustomers, Customer } from '../mockData/ecommerce';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
};

export default function CustomersPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const statuses = ['active', 'inactive'];

  const filteredCustomers = mockCustomers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(search.toLowerCase()) ||
                          customer.email.toLowerCase().includes(search.toLowerCase()) ||
                          customer.address.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Sort by total spent
  const sortedCustomers = [...filteredCustomers].sort((a, b) => b.totalSpent - a.totalSpent);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Customers</Title>
          <Text c="dimmed" size="sm">{filteredCustomers.length} customers found</Text>
        </Box>
        <Button leftSection={<IconUser size={16} />}>Add Customer</Button>
      </Group>

      {/* Stats */}
      <Group grow>
        <Card>
          <Text size="sm" c="dimmed">Total Customers</Text>
          <Text size="xl" fw={700}>{mockCustomers.length}</Text>
        </Card>
        <Card>
          <Text size="sm" c="dimmed">Active Customers</Text>
          <Text size="xl" fw={700}>{mockCustomers.filter(c => c.status === 'active').length}</Text>
        </Card>
        <Card>
          <Text size="sm" c="dimmed">Total Revenue</Text>
          <Text size="xl" fw={700}>{formatCurrency(mockCustomers.reduce((acc, c) => acc + c.totalSpent, 0))}</Text>
        </Card>
        <Card>
          <Text size="sm" c="dimmed">Avg. Lifetime Value</Text>
          <Text size="xl" fw={700}>
            {formatCurrency(mockCustomers.reduce((acc, c) => acc + c.totalSpent, 0) / mockCustomers.length)}
          </Text>
        </Card>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search customers..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={130}
          />
        </Group>
      </Card>

      {/* Customers Table */}
      <Card p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Contact</Table.Th>
              <Table.Th>Orders</Table.Th>
              <Table.Th>Total Spent</Table.Th>
              <Table.Th>Last Order</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th w={50}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedCustomers.map((customer) => (
              <Table.Tr key={customer.id}>
                <Table.Td>
                  <Group gap="sm">
                    <Avatar size="sm" radius="xl" color="blue">
                      {customer.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Box>
                      <Text size="sm" fw={500}>{customer.name}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{customer.address}</Text>
                    </Box>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Group gap={4}>
                      <IconMail size={12} color="var(--mantine-color-dimmed)" />
                      <Text size="xs">{customer.email}</Text>
                    </Group>
                    <Group gap={4}>
                      <IconPhone size={12} color="var(--mantine-color-dimmed)" />
                      <Text size="xs">{customer.phone}</Text>
                    </Group>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{customer.totalOrders} orders</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>{formatCurrency(customer.totalSpent)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {customer.lastOrderAt ? formatDate(customer.lastOrderAt) : 'Never'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={customer.status === 'active' ? 'green' : 'gray'}>
                    {customer.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray">
                        <IconDotsVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconEye size={14} />}>View Profile</Menu.Item>
                      <Menu.Item leftSection={<IconMail size={14} />}>Send Email</Menu.Item>
                      <Menu.Item leftSection={<IconPhone size={14} />}>Call</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
